import { UIBrandedFare, UIFlightData } from "@flights/types";
import { t } from "@bookingcom/lingojs-core";
import React, { useMemo } from "react";
import { Text } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import useFormatPrice from "../../../../hooks/useFormatPrice";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price";
import { isOfMetaOrigin } from "../../../../utils/marketing-url-params";
import useUserAgent from "../../../../hooks/useUserAgent";
import useRouteName from "../../../../hooks/useRouteName";
import useGetSearchResultsBound from "../../../../hooks/useGetSearchResultsBound";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { UnifiedPriceTag, UnifiedPriceTagProps } from "../../UnifiedPriceTag";
import { getAttachedAncillaries } from "../../UnifiedPriceBreakdown";

type FareCardPriceProps = {
  fareOffer: UIBrandedFare;
  baseOffer: UIFlightData;
  isBold?: boolean;
  /* start - flights_web_flight_card_update_desktop */
  isFlightCardUpdate?: boolean;
  /* end - flights_web_flight_card_update_desktop */
};

const FareCardPrice = (props: FareCardPriceProps) => {
  const { fareOffer, baseOffer, isBold, isFlightCardUpdate = false } = props;
  const i18n = useI18n();
  const { formatPrice } = useFormatPrice();

  const routeName = useRouteName();
  const { isMobile } = useUserAgent();

  const adultTravellerPrice = getLowestAdultTravellerPrice(fareOffer.travellerPrices);
  const isRoundPrice = isFlightCardUpdate || (!isOfMetaOrigin() && isMobile);

  const totalPrice = useMemo(() => {
    return isRoundPrice && fareOffer.priceBreakdown.totalRounded
      ? formatPrice(fareOffer.priceBreakdown.totalRounded, { decimalPlaces: 0 })
      : formatPrice(fareOffer.priceBreakdown.total);
  }, [fareOffer.priceBreakdown.total, fareOffer.priceBreakdown.totalRounded, formatPrice, isRoundPrice]);

  const bound = useGetSearchResultsBound();

  const shouldHidePriceExplanation = useMemo<boolean>(() => {
    const isSolo = [...fareOffer.travellerPrices].length === 1;
    if (
      ![
        "searchresults",
        "flightdetails",
        /* start - flights_split_bounds_us */
        "searchresults-outbound",
        "searchresults-inbound",
        "flightdetails-outbound",
        "flightdetails-inbound"
        /* end - flights_split_bounds_us */
      ].includes(routeName) ||
      !isSolo
    ) {
      return false;
    }
    return true;
  }, [fareOffer.travellerPrices, routeName]);

  const priceTagProps = useMemo((): UnifiedPriceTagProps => {
    const ticketPrice = fareOffer.priceBreakdown;

    const productId = fareOffer.unifiedPriceBreakdown?.id;

    const price = isRoundPrice && ticketPrice.totalRounded ? ticketPrice.totalRounded : ticketPrice.total;

    return {
      productId,
      price,
      shouldRoundPrice: isRoundPrice
    };
  }, [fareOffer.priceBreakdown, isRoundPrice, fareOffer.unifiedPriceBreakdown?.id]);

  const priceTagPropsWithPricePerAdult = useMemo((): UnifiedPriceTagProps => {
    if (!adultTravellerPrice) return priceTagProps;

    const multipleTravellers = fareOffer.travellerPrices.length > 1;

    const pricePerAdult =
      isRoundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
        : adultTravellerPrice.travellerPriceBreakdown.total;

    if (bound === "inbound") {
      const baseAdultTravellerPrice = getLowestAdultTravellerPrice(baseOffer.travellerPrices)?.travellerPriceBreakdown;
      const fareAdultTravellerPrice = getLowestAdultTravellerPrice(fareOffer.travellerPrices)?.travellerPriceBreakdown;

      if (baseAdultTravellerPrice && fareAdultTravellerPrice) {
        let finalPricePerAdult;
        if (isRoundPrice && fareAdultTravellerPrice.totalRounded && baseAdultTravellerPrice.totalRounded) {
          finalPricePerAdult = pricingUtils.subtractPrice(
            fareAdultTravellerPrice.totalRounded,
            baseAdultTravellerPrice.totalRounded
          );
        } else {
          finalPricePerAdult = pricingUtils.subtractPrice(fareAdultTravellerPrice.total, baseAdultTravellerPrice.total);
        }

        const pricePerAdultTranslations = [
          (formattedPrice: string) => formattedPrice.replace("-", ""),
          (formattedPrice: string) =>
            finalPricePerAdult.units < 0
              ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
              : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
        ];

        return {
          ...priceTagProps,
          pricePerAdult: finalPricePerAdult,
          pricePerAdultTranslations,
          shouldShowPricePerAdult: !!adultTravellerPrice
        };
      }
    }

    return {
      ...priceTagProps,
      pricePerAdult,
      shouldShowPricePerAdult: multipleTravellers && !!adultTravellerPrice
    };
  }, [priceTagProps, adultTravellerPrice, baseOffer, fareOffer.travellerPrices, isRoundPrice, bound, i18n]);

  const ariaAttributes = useMemo(() => {
    const attributes: Partial<Record<"aria-label", string>> = {};

    if (bound === "inbound") {
      const totalPrice = formatPrice(priceTagPropsWithPricePerAdult.price);
      const pricePerPerson = priceTagPropsWithPricePerAdult.pricePerAdult
        ? formatPrice(priceTagPropsWithPricePerAdult.pricePerAdult)
        : "";

      const formattedPriceDiff = priceTagPropsWithPricePerAdult?.pricePerAdultTranslations?.[1](pricePerPerson) ?? "";

      attributes["aria-label"] = `${formattedPriceDiff} ${i18n.trans(
        t("flights_sr_price_multi_total", { variables: { total_price: totalPrice } })
      )}`;

      return attributes;
    }

    const perPersonPrice = adultTravellerPrice
      ? formatPrice(adultTravellerPrice.travellerPriceBreakdown.total)
      : formatPrice(fareOffer.priceBreakdown.total);

    const totalPriceLabel = i18n.trans(t("a11y_flights_apex_price_per_traveller"));

    const perTravellerPriceLabel = `${totalPriceLabel}, ${
      isFlightCardUpdate
        ? i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: totalPrice } }))
        : i18n.trans(t("flights_sr_price_multi", { variables: { total_price: totalPrice } }))
    }`;

    const label = fareOffer.travellerPrices.length > 1 ? perTravellerPriceLabel : totalPriceLabel;

    attributes["aria-label"] = `${perPersonPrice} ${label}`;

    return attributes;
  }, [
    bound,
    adultTravellerPrice,
    formatPrice,
    fareOffer.priceBreakdown.total,
    fareOffer.travellerPrices.length,
    i18n,
    isFlightCardUpdate,
    totalPrice,
    priceTagPropsWithPricePerAdult.price,
    priceTagPropsWithPricePerAdult.pricePerAdult,
    priceTagPropsWithPricePerAdult?.pricePerAdultTranslations
  ]);

  const unifiedPriceTagPriceProps = useMemo((): UnifiedPriceTagProps => {
    const pricePropsVariant = isBold ? "strong_2" : "strong_1";
    const pricePropsAttributes = {
      "aria-hidden": true,
      "data-testid": "flight_card_price_main_price"
    };

    const attachedAncillaries = getAttachedAncillaries(fareOffer.unifiedPriceBreakdown);

    return {
      ...priceTagPropsWithPricePerAdult,
      priceAlignment: "start",
      priceProps: {
        attributes: pricePropsAttributes,
        tagName: "div",
        variant: pricePropsVariant
      },
      attachedAncillaries
    };
  }, [priceTagPropsWithPricePerAdult, isBold, fareOffer.unifiedPriceBreakdown]);

  const unifiedPriceTagExplanationProps = useMemo((): Partial<UnifiedPriceTagProps> | undefined => {
    if (adultTravellerPrice) {
      const pricePerAdultExplanationProps: UnifiedPriceTagProps["pricePerAdultExplanationProps"] = {
        variant: "small_1",
        attributes: {
          "aria-hidden": true,
          "data-testid": "flight_card_price_total_price"
        }
      };

      const pricePerAdultExplanationTranslations = [];

      if (bound === "inbound") {
        pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
          i18n.trans(t("flights_sr_split_bounds_us_total", { variables: { price: formattedPrice } }))
        );
      } else {
        if (!shouldHidePriceExplanation) {
          pricePerAdultExplanationTranslations.push((formattedPrice: string) =>
            fareOffer.travellerPrices.length > 1
              ? isFlightCardUpdate
                ? i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: formattedPrice } }))
                : i18n.trans(t("flights_sr_price_multi", { variables: { total_price: formattedPrice } }))
              : i18n.trans(t("flights_price_qualifier_total_for_all"))
          );
        }
      }

      return {
        pricePerAdultExplanationProps,
        pricePerAdultExplanationTranslations
      };
    }
    return;
  }, [
    adultTravellerPrice,
    bound,
    i18n,
    shouldHidePriceExplanation,
    fareOffer.travellerPrices.length,
    isFlightCardUpdate
  ]);

  return (
    <div>
      {bound === "inbound" && (
        <Text variant="small_1" color="neutral_alt">
          {fareOffer.travellerPrices.length > 1
            ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
            : i18n.trans(t("flights_sr_split_bounds_us_round_trip"))}
        </Text>
      )}
      <UnifiedPriceTag
        {...unifiedPriceTagPriceProps}
        {...unifiedPriceTagExplanationProps}
        attributes={ariaAttributes}
      />
    </div>
  );
};

export default FareCardPrice;
